import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  Box, TextField, Button, Typography, CircularProgress,
  IconButton, InputAdornment,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";

const SPECIAL_CODE = "kal1232025a!!";

const inputSx = {
  mb: 1.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px", bgcolor: "#fafafa",
    "& fieldset": { borderColor: "#dbdbdb" },
    "&:hover fieldset": { borderColor: "#bbb" },
    "&.Mui-focused fieldset": { borderColor: "#EB578A", borderWidth: 1.5 },
  },
};

export default function Register() {
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [validToken, setValidToken] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token");

  useEffect(() => {
    if (!token) {
      setValidToken(false);
      setError("Brak tokena rejestracyjnego.");
    } else if (token === SPECIAL_CODE) {
      setValidToken(true);
    } else {
      axios
        .get(`https://twojastrona.pl/api/validate_registration_token.php?token=${token}`)
        .then(res => setValidToken(res.data.valid))
        .catch(() => { setValidToken(false); setError("Token nieważny lub już wykorzystany."); });
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = { ...form, token };
      if (token === SPECIAL_CODE) { payload.image_limit = 300; payload.expires_months = 1; }
      await axios.post("https://twojastrona.pl/api/register.php", payload);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch {
      setError("Błąd rejestracji. Spróbuj ponownie.");
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: "#fff",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      px: 3, py: 4,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: "100%", maxWidth: 360 }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 72, height: 72, borderRadius: 22,
            background: "linear-gradient(135deg, #EB578A 0%, #C0145E 100%)",
            mb: 2, boxShadow: "0 10px 30px rgba(235,87,138,0.4)",
          }}>
            <FavoriteIcon sx={{ color: "#fff", fontSize: 36 }} />
          </Box>
          <Typography sx={{
            fontSize: 26, fontWeight: 900, letterSpacing: "-0.5px",
            background: "linear-gradient(90deg, #EB578A, #C0145E)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            Księga Gości
          </Typography>
        </Box>

        {/* Content */}
        {validToken === null && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#EB578A", mb: 2 }} />
            <Typography sx={{ color: "#888", fontSize: 14 }}>Weryfikuję zaproszenie…</Typography>
          </Box>
        )}

        {validToken === false && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ color: "#e53935", fontWeight: 600, mb: 1 }}>
              {error || "Token nieważny."}
            </Typography>
            <Typography sx={{ color: "#aaa", fontSize: 14 }}>Użyj poprawnego linku zaproszenia.</Typography>
          </Box>
        )}

        {validToken && success && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: "#EB578A", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a2e", mb: 0.5 }}>
              Witaj w Księdze! 🎉
            </Typography>
            <Typography sx={{ color: "#aaa", fontSize: 14 }}>
              Za chwilę zostaniesz przekierowany…
            </Typography>
          </Box>
        )}

        {validToken && !success && (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 0.5, color: "#1a1a2e", textAlign: "center" }}>
              Utwórz konto
            </Typography>
            <Typography sx={{ color: "#aaa", fontSize: 14, mb: 3, textAlign: "center" }}>
              Dołącz do Księgi Gości 🎉
            </Typography>

            {error && (
              <Typography sx={{ color: "#e53935", fontSize: 13, mb: 1.5, textAlign: "center", fontWeight: 500 }}>
                {error}
              </Typography>
            )}

            <form onSubmit={handleSubmit}>
              <TextField fullWidth placeholder="E-mail" name="email" type="email"
                variant="outlined" value={form.email} onChange={handleChange} required sx={inputSx} />
              <TextField fullWidth placeholder="Imię" name="firstName"
                variant="outlined" value={form.firstName} onChange={handleChange} required sx={inputSx} />
              <TextField fullWidth placeholder="Nazwisko" name="lastName"
                variant="outlined" value={form.lastName} onChange={handleChange} required sx={inputSx} />
              <TextField
                fullWidth placeholder="Hasło" name="password"
                type={showPass ? "text" : "password"}
                variant="outlined" value={form.password} onChange={handleChange} required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass(s => !s)} size="small" tabIndex={-1} edge="end">
                        {showPass
                          ? <VisibilityOffIcon fontSize="small" sx={{ color: "#aaa" }} />
                          : <VisibilityIcon fontSize="small" sx={{ color: "#aaa" }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <Button
                fullWidth type="submit"
                sx={{
                  mt: 0.5, py: 1.5,
                  background: "linear-gradient(90deg, #EB578A 0%, #C0145E 100%)",
                  color: "#fff", fontWeight: 700, fontSize: 15,
                  borderRadius: "12px", textTransform: "none",
                  boxShadow: "0 6px 20px rgba(235,87,138,0.4)",
                  "&:hover": { background: "linear-gradient(90deg, #d94d7e 0%, #a81050 100%)" },
                }}
              >
                Zarejestruj się
              </Button>
            </form>
          </>
        )}

        {/* Login link */}
        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #efefef", textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, color: "#555" }}>
            Masz już konto?{" "}
            <Box component={Link} to="/login" sx={{
              color: "#EB578A", fontWeight: 700, textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}>
              Zaloguj się
            </Box>
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
}
